import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { DataTable, Column } from '@/components/DataTable';
import { ButtonMenu } from '@/components/ButtonMenu';
import { Modal } from '@/components/Modal';
import { FormField } from '@/components/FormField';
import { tiktokService, TiktokCampaign } from '@/services/tiktok.service';
import { LastSyncedBadge } from '@/components/LastSyncedBadge';
import { Music2, RefreshCw, Power, ExternalLink, Plus, Sparkles, Layers, Wand2, AlertTriangle } from 'lucide-react';
import { TIKTOK_MATRIX, objectiveSpec, type TiktokObjective } from '@/lib/tiktok-matrix';
import clsx from 'clsx';
import toast from 'react-hot-toast';

type ObjectiveType = TiktokObjective;
type BudgetMode = 'BUDGET_MODE_DAY' | 'BUDGET_MODE_TOTAL' | 'BUDGET_MODE_INFINITE';

// Plain-language copy for each objective. The list itself, the order, and
// which ones are usable all come from the matrix — never hardcode that.
const OBJECTIVE_HINTS: Record<ObjectiveType, string> = {
  TRAFFIC:         'Drive clicks to a website or app store.',
  VIDEO_VIEWS:     'Optimise for 6s / 15s engaged video views.',
  REACH:           'Show ads to as many unique users as possible.',
  ENGAGEMENT:      'Followers and profile visits.',
  CONVERSIONS:     'Optimise for purchases / sign-ups via Pixel.',
  LEAD_GENERATION: 'In-feed lead form.',
  PRODUCT_SALES:   'Catalog-driven shopping ads.',
  APP_PROMOTION:   'Installs + in-app actions.',
};

/** Short suffix for objectives blocked by a missing account asset. */
const MISSING_ASSET_SUFFIX: Record<'CATALOG' | 'APP' | 'LEAD_FORM', string> = {
  CATALOG: 'unavailable — needs a product catalogue',
  APP: 'unavailable — needs a registered app',
  LEAD_FORM: 'unavailable — needs an Instant Form',
};

const OBJECTIVES = TIKTOK_MATRIX.map((o) => ({
  value: o.objective,
  label: o.label,
  hint: OBJECTIVE_HINTS[o.objective],
  // PRODUCT_SALES / APP_PROMOTION fail at ad-group time without the asset, so
  // they're offered as disabled rather than silently allowed.
  disabled: !!o.requiresAccountAsset,
  unavailableReason: o.unavailableReason,
  warning: o.warning,
  badge: o.badge,
  assetSuffix: o.requiresAccountAsset ? MISSING_ASSET_SUFFIX[o.requiresAccountAsset] : undefined,
}));

const BUDGET_MODES: Array<{ value: BudgetMode; label: string; hint: string }> = [
  { value: 'BUDGET_MODE_DAY',      label: 'Daily',     hint: 'Spend up to this amount per day.' },
  { value: 'BUDGET_MODE_TOTAL',    label: 'Lifetime',  hint: 'Total budget over the campaign\'s run.' },
  { value: 'BUDGET_MODE_INFINITE', label: 'No cap',    hint: 'Set the budget at the ad-group level.' },
];

const inputCls =
  'w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary';

const STATUS_COLORS: Record<string, string> = {
  ENABLE:  'bg-success-soft text-success',
  DISABLE: 'bg-surface-alt text-text-muted',
};

type Props = {
  /** Rendered inside TiktokAdsManager's tab shell — suppress the page Topbar. */
  embedded?: boolean;
  onDrillToAdGroups?: (tiktokCampaignId: string) => void;
  /** Set by the parent's Create-campaign menu to open the advanced modal. */
  autoOpenCreate?: boolean;
  onAutoOpenConsumed?: () => void;
};

export default function TiktokCampaigns({
  embedded = false,
  onDrillToAdGroups,
  autoOpenCreate = false,
  onAutoOpenConsumed,
}: Props = {}) {
  const [campaigns, setCampaigns] = useState<TiktokCampaign[] | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Create-campaign modal state
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<{
    campaignName: string;
    objectiveType: ObjectiveType;
    budgetMode: BudgetMode;
    budget: string; // string in form, parsed on submit
    operationStatus: 'ENABLE' | 'DISABLE';
  }>({
    campaignName: '',
    objectiveType: 'TRAFFIC',
    budgetMode: 'BUDGET_MODE_DAY',
    budget: '',
    operationStatus: 'DISABLE',
  });

  const resetForm = () => setForm({
    campaignName: '',
    objectiveType: 'TRAFFIC',
    budgetMode: 'BUDGET_MODE_DAY',
    budget: '',
    operationStatus: 'DISABLE',
  });

  const selectedObjective = OBJECTIVES.find((o) => o.value === form.objectiveType) ?? null;

  const refresh = () =>
    tiktokService.listCampaigns().then(setCampaigns).catch(() => setCampaigns([]));

  // On first mount, load cached data. If empty or older than 10 min, trigger
  // a background sync so the dashboard isn't stale by default.
  useEffect(() => {
    refresh().then(() => {
      tiktokService.listCampaigns()
        .then((rows) => {
          const newest = rows
            .map((r) => Date.parse(r.lastSyncedAt ?? ''))
            .filter((n) => Number.isFinite(n))
            .reduce((a, b) => Math.max(a, b), 0);
          const stale = !newest || Date.now() - newest > 10 * 60 * 1000;
          if (rows.length === 0 || stale) silentSync();
        })
        .catch(() => undefined);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const silentSync = async () => {
    try {
      await tiktokService.syncCampaigns();
      await refresh();
    } catch { /* user can retry via the manual button */ }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await tiktokService.syncCampaigns();
      toast.success(`Synced ${r.total} campaigns — ${r.created} new, ${r.updated} updated`);
      await refresh();
    } catch {
      // Axios interceptor already toasted the backend message; avoid duplicates.
    } finally { setSyncing(false); }
  };

  const submitCreate = async () => {
    const name = form.campaignName.trim();
    if (!name) { toast.error('Campaign name is required'); return; }
    // An objective whose account asset is missing fails later at ad-group
    // time — stop it here instead of leaving an unusable campaign behind.
    const spec = objectiveSpec(form.objectiveType);
    if (spec?.unavailableReason) {
      toast.error(`TikTok can't run ${spec.label} campaigns on this advertiser. ${spec.unavailableReason}`);
      return;
    }
    const needsBudget = form.budgetMode !== 'BUDGET_MODE_INFINITE';
    const budgetNum = form.budget.trim() ? Number(form.budget) : undefined;
    if (needsBudget) {
      if (!budgetNum || !Number.isFinite(budgetNum) || budgetNum <= 0) {
        toast.error('Enter a positive budget');
        return;
      }
    }
    setCreating(true);
    try {
      const r = await tiktokService.createCampaign({
        campaignName: name,
        objectiveType: form.objectiveType,
        budgetMode: form.budgetMode,
        budget: needsBudget ? budgetNum : undefined,
        operationStatus: form.operationStatus,
      });
      toast.success(`Campaign created · TikTok id ${r.campaignId}`);
      setCreateOpen(false);
      resetForm();
      await refresh();
    } catch {
      // axios interceptor toasts the backend message
    } finally { setCreating(false); }
  };

  const toggleStatus = async (c: TiktokCampaign) => {
    setBusyId(c.tiktokCampaignId);
    const next = c.operationStatus === 'ENABLE' ? 'DISABLE' : 'ENABLE';
    try {
      await tiktokService.setCampaignStatus(c.tiktokCampaignId, next);
      toast.success(`Campaign ${next === 'ENABLE' ? 'enabled' : 'paused'}`);
      await refresh();
    } catch {
      // Axios interceptor already toasted the backend message; avoid duplicates.
    } finally { setBusyId(null); }
  };

  const columns: Column<TiktokCampaign>[] = [
    {
      key: 'name',
      header: 'Campaign',
      width: 'minmax(220px, 2.2fr)',
      render: (c) => (
        <div className="flex items-center gap-2.5">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-surface-alt text-text-primary">
            <Music2 size={13} />
          </span>
          <span className="min-w-0">
            <span className="block truncate font-medium text-text-primary">{c.campaignName}</span>
            <span className="block truncate font-mono text-2xs text-text-muted">{c.tiktokCampaignId}</span>
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: 'minmax(90px, 0.7fr)',
      render: (c) =>
        c.operationStatus ? (
          <span className={clsx('inline-block rounded-full px-2 py-0.5 text-2xs font-semibold', STATUS_COLORS[c.operationStatus] ?? 'bg-surface-alt text-text-muted')}>
            {c.operationStatus}
          </span>
        ) : <span className="text-text-muted">—</span>,
    },
    {
      key: 'objective',
      header: 'Objective',
      width: 'minmax(110px, 0.9fr)',
      render: (c) =>
        c.objectiveType ? (
          <span className="inline-block rounded-full bg-info-soft px-2 py-0.5 text-2xs text-info">{c.objectiveType}</span>
        ) : <span className="text-text-muted">—</span>,
    },
    {
      key: 'budget',
      header: 'Budget',
      width: 'minmax(120px, 0.9fr)',
      render: (c) =>
        c.budget != null ? (
          <span className="tabular-nums text-text-primary">
            {c.budget.toLocaleString()}
            <span className="ml-1 text-2xs text-text-muted">{BUDGET_MODE_LABEL[c.budgetMode ?? ''] ?? c.budgetMode ?? ''}</span>
          </span>
        ) : <span className="text-text-muted">—</span>,
    },
    {
      key: 'modified',
      header: 'Modified',
      width: 'minmax(90px, 0.7fr)',
      render: (c) => (
        <span className="text-xs text-text-secondary">
          {(c.modifyTime ?? c.createTime) ? new Date((c.modifyTime ?? c.createTime)!).toLocaleDateString() : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: 'minmax(190px, auto)',
      render: (c) => (
        <div className="flex items-center justify-end gap-2">
          {onDrillToAdGroups && (
            <button
              onClick={() => onDrillToAdGroups(c.tiktokCampaignId)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 py-1 text-xs font-medium text-text-secondary transition-colors hover:border-primary hover:text-primary"
            >
              <Layers size={11} /> Ad groups
            </button>
          )}
          <button
            onClick={() => toggleStatus(c)}
            disabled={busyId === c.tiktokCampaignId}
            className={clsx(
              'inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors',
              c.operationStatus === 'ENABLE'
                ? 'border-success/30 bg-success-soft text-success hover:bg-success-soft/70'
                : 'border-border bg-surface-alt text-text-muted hover:bg-border',
              busyId === c.tiktokCampaignId && 'cursor-wait opacity-60',
            )}
          >
            <Power size={11} /> {c.operationStatus === 'ENABLE' ? 'Pause' : 'Enable'}
          </button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (autoOpenCreate) {
      setCreateOpen(true);
      onAutoOpenConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenCreate]);

  const actions = (
    <div className="flex items-center gap-2">
      <LastSyncedBadge timestamps={(campaigns ?? []).map(c => c.lastSyncedAt)} />
      <Button variant="secondary" onClick={sync} loading={syncing}>
        <RefreshCw size={14} /> Sync
      </Button>
      {/* Standalone use keeps its own create menu; when embedded the parent's
          Topbar owns it so the action isn't offered twice on one screen. */}
      {!embedded && <CreateCampaignMenu onAdvanced={() => setCreateOpen(true)} />}
    </div>
  );

  return (
    <>
      {embedded ? (
        <div className="flex justify-end px-7 pt-4">{actions}</div>
      ) : (
        <Topbar
          title="TikTok Campaigns"
          breadcrumb="TikTok Ads"
          description="Cached snapshot of campaigns from your default TikTok advertiser. Sync to refresh."
          right={actions}
        />
      )}

      <div className={clsx('w-full space-y-4 px-7', embedded ? 'pb-24 pt-4' : 'py-7')}>
        {campaigns === null && <Card><div className="skeleton h-24 rounded-lg" /></Card>}

        {campaigns && campaigns.length === 0 && (
          <EmptyState
            icon={<Music2 size={40} className="text-text-muted" />}
            title="No campaigns yet"
            description="Create one here in SlightorCRM, or build it in TikTok Ads Manager and Sync it across."
            action={
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button variant="primary" onClick={() => setCreateOpen(true)}>
                  <Plus size={14} /> New campaign
                </Button>
                <Button variant="secondary" onClick={sync} loading={syncing}>
                  <RefreshCw size={14} /> Sync now
                </Button>
                <a href="https://ads.tiktok.com/i18n/dashboard/campaign" target="_blank" rel="noreferrer">
                  <Button variant="ghost"><ExternalLink size={14} /> Open Ads Manager</Button>
                </a>
              </div>
            }
          />
        )}

        {campaigns && campaigns.length > 0 && (
          <DataTable rows={campaigns} columns={columns} stickyHeader />
        )}
      </div>

      {/* ── Create campaign modal ──────────────────────────────────────── */}
      <Modal
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        title="New TikTok campaign"
      >
        <div className="space-y-4">
          <p className="rounded-lg border border-border bg-surface-alt/50 p-3 text-xs text-text-secondary">
            Creates a campaign on your <strong>default</strong> connected advertiser. The campaign starts paused so
            you can configure ad groups and ads before it goes live.
          </p>

          <FormField label="Campaign name" required>
            <input
              className={inputCls}
              placeholder="e.g. Eid Sale 2026 — Traffic"
              value={form.campaignName}
              onChange={(e) => setForm((f) => ({ ...f, campaignName: e.target.value }))}
              autoFocus
            />
          </FormField>

          <FormField
            label="Objective"
            required
            hintBelow
            hint={selectedObjective?.unavailableReason ?? selectedObjective?.warning ?? selectedObjective?.hint}
          >
            <select
              className={inputCls}
              value={form.objectiveType}
              onChange={(e) => setForm((f) => ({ ...f, objectiveType: e.target.value as ObjectiveType }))}
            >
              {OBJECTIVES.map((o) => (
                <option key={o.value} value={o.value} disabled={o.disabled} title={o.unavailableReason ?? o.warning}>
                  {o.label}{o.badge ? ` — ${o.badge}` : ''}{o.assetSuffix && !o.badge ? ` — ${o.assetSuffix}` : ''}
                </option>
              ))}
            </select>
          </FormField>

          {selectedObjective?.unavailableReason && (
            <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-2xs text-warning">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              {selectedObjective.unavailableReason}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Budget mode" required hintBelow hint={BUDGET_MODES.find((b) => b.value === form.budgetMode)?.hint}>
              <select
                className={inputCls}
                value={form.budgetMode}
                onChange={(e) => setForm((f) => ({ ...f, budgetMode: e.target.value as BudgetMode }))}
              >
                {BUDGET_MODES.map((b) => (
                  <option key={b.value} value={b.value}>{b.label}</option>
                ))}
              </select>
            </FormField>

            <FormField
              label={form.budgetMode === 'BUDGET_MODE_INFINITE' ? 'Budget (set on ad-group)' : 'Budget'}
              hintBelow hint="Use your advertiser's currency. e.g. 50 for AED 50/day."
            >
              <input
                className={clsx(inputCls, form.budgetMode === 'BUDGET_MODE_INFINITE' && 'opacity-50')}
                type="number"
                min={0}
                step="any"
                placeholder={form.budgetMode === 'BUDGET_MODE_INFINITE' ? '—' : '50'}
                value={form.budget}
                onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))}
                disabled={form.budgetMode === 'BUDGET_MODE_INFINITE'}
              />
            </FormField>
          </div>

          <FormField label="Start status" hintBelow hint="DISABLE is the safer default — flip to ENABLE only after reviewing all ad groups and ads.">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, operationStatus: 'DISABLE' }))}
                className={clsx(
                  'flex-1 rounded-xl border px-3 py-2 text-sm transition-colors',
                  form.operationStatus === 'DISABLE'
                    ? 'border-primary bg-primary-soft text-primary'
                    : 'border-border bg-surface text-text-primary hover:border-primary/40',
                )}
              >
                Paused (recommended)
              </button>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, operationStatus: 'ENABLE' }))}
                className={clsx(
                  'flex-1 rounded-xl border px-3 py-2 text-sm transition-colors',
                  form.operationStatus === 'ENABLE'
                    ? 'border-success bg-success-soft text-success'
                    : 'border-border bg-surface text-text-primary hover:border-success/40',
                )}
              >
                Active immediately
              </button>
            </div>
          </FormField>

          <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
            <Button variant="ghost" onClick={() => { resetForm(); setCreateOpen(false); }} disabled={creating}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submitCreate} loading={creating}>
              Create campaign
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

const BUDGET_MODE_LABEL: Record<string, string> = {
  BUDGET_MODE_DAY: '/day',
  BUDGET_MODE_TOTAL: 'lifetime',
  BUDGET_MODE_INFINITE: 'no cap',
};

/**
 * One entry point for the two ways to create a campaign. They do different
 * amounts of work — the wizard builds campaign + ad group + ad and can go live;
 * the advanced modal creates only an empty campaign shell — so each says so
 * rather than relying on the words "Start" vs "Advanced" to convey it.
 */
export function CreateCampaignMenu({
  onAdvanced,
  onGuided,
}: {
  onAdvanced: () => void;
  onGuided?: () => void;
}) {
  const nav = useNavigate();
  return (
    <ButtonMenu
      label="Create campaign"
      iconLeft={<Plus size={14} />}
      items={[
        {
          label: 'Guided campaign',
          description: 'Campaign + ad group + ad in four steps. Can launch straight away.',
          icon: <Wand2 size={13} />,
          onSelect: () => (onGuided ? onGuided() : nav('/tiktok/campaigns/new-wizard')),
        },
        {
          label: 'Campaign only (advanced)',
          description: 'Just the campaign shell, created paused. You add ad groups and ads yourself.',
          icon: <Sparkles size={13} />,
          onSelect: onAdvanced,
        },
      ]}
    />
  );
}
